// @flow

import * as React from 'react';

import { connect } from 'react-redux';
import { localize } from '@ta/common.i18n';
import { ReadMore } from '@ta/common.text';
import { Icon } from '@ta/styleguide.icon';
import type { ViewportCategory } from '@ta/common.state';
import { Linkify } from '@ta/social.links';
import { MemberEventOnObjectBlock } from '@ta/social.member-event';
import { InteractionLogger } from '@ta/tracking.interactions';

import { CardSection, FeedSectionPhotoCarousel } from '@ta/social.section-core';
import type { PhotoWithMetaData, Sponsorship } from '@ta/social.section-core';
import styles from './MediaBatchPhotoSection.less';

type MediaBatchPhotoSectionLayout = 'FULL' | 'REPOST_NO_MODAL' | 'REPOST_WITH_MODAL';

type Metadata = {|
  mediaBatchId: any,
  caption: any,
  socialReferences: any,
  socialStatistics: any,
  orderedLocations: any,
  publishedDate?: any,
  uploadDate?: any,
  created?: any,
  __typename: any,
  route: any,
|};

/** External props for a media batch section */
export type ExternalProps = {|
  member: any,
  actor: any,
  actorForUrl?: any,
  metadata: Metadata,
  photos: Array<PhotoWithMetaData>,
  deleteObject: ?() => void,
  untagObject: ?() => void,
  isSingleObject: boolean,
  layout: MediaBatchPhotoSectionLayout,
  sponsorship?: Sponsorship,
  socialStatisticsBar?: React.Node,
  socialActionBar?: React.Node,
|};

/** Potential state information to manage */
export type MediaBatchPhotoSectionManagedProps = {|
  currentlyDisplayedImageIndex: number,
  setCurrentlyDisplayedImageIndex: number => void,
|};

export type MediaBatchPhotoSectionProps = {|
  ...ExternalProps,
  ...MediaBatchPhotoSectionManagedProps,
|};

type Props = {|
  ...MediaBatchPhotoSectionProps,
  loggedInUserId: ?string,
  viewportCategory?: ViewportCategory,
|};

const MediaBatchPhotoSection = (props: Props) => {
  const metadata = {
    numPhotos: props.photos.length,
    caption: props?.metadata?.caption,
    id: props.metadata.mediaBatchId,
    socialReferences: props.metadata.socialReferences,
    socialStatistics: props.metadata.socialStatistics,
    orderedLocations: props.metadata.orderedLocations,
    createdDate: props.metadata.created,
    __typename: props.metadata.__typename,
    route: props.metadata.route,
  };

  // FlowBug: An exact type that is a superset of an inexact type cannot be implicitly converted to the inexact type, so cast to any first
  const overflowMenuObjectForType = {
    REPORT: (props.photos[props.currentlyDisplayedImageIndex]: any),
  };

  const header = (
    <MemberEventOnObjectBlock
      objectEventType="onMediaBatch"
      member={props.member}
      object={metadata}
      actor={props.actor}
      deleteObject={props.deleteObject}
      untagObject={props.untagObject}
      loggedInUserId={props.loggedInUserId}
      isEmbedded={props.layout === 'REPOST_NO_MODAL' || props.layout === 'REPOST_WITH_MODAL'}
      viewportCategory={props.viewportCategory}
      overflowMenuObjectForType={overflowMenuObjectForType}
    />
  );

  const MAX_CAPTION_LENGTH = 160;

  const cta = () => (
    <div className={styles.ctaLine}>
      <span className={styles.cta}>{localize('common_Read_more') /* Read more */}</span>
      <Icon name="caret-down" className="" />
    </div>
  );

  const currentCaption = metadata.caption
    ? metadata.caption
    : props?.photos?.[props.currentlyDisplayedImageIndex]?.caption || '';
  const currentSocialReferences = metadata.caption
    ? metadata.socialReferences
    : props?.photos?.[props.currentlyDisplayedImageIndex]?.socialReferences;

  const content = [
    <CardSection.Content fullWidth key="mediaBatchPhoto">
      <InteractionLogger>
        {logInteraction => (
          <FeedSectionPhotoCarousel
            actor={props.actor}
            actorForUrl={props.actorForUrl}
            deleteObject={props.deleteObject}
            untagObject={props.untagObject}
            member={props.member}
            metadata={metadata}
            photos={props.photos}
            currentlyDisplayedIndex={props.currentlyDisplayedImageIndex}
            onPhotoChange={(index, e) => {
              if (index > props.currentlyDisplayedImageIndex && e) {
                // User scrolled to the next picture
                logInteraction(e, 'batchGalleryScroll', {
                  slot: index + 1, // 1 based
                  itemCount: metadata.numPhotos,
                });
              }
              props.setCurrentlyDisplayedImageIndex(index);
            }}
            socialStatisticsBar={props.socialStatisticsBar}
            socialActionBar={props.socialActionBar}
            overflowMenuObject={metadata}
            // FlowBug: An exact type that is a superset of an inexact type cannot be implicitly converted to the inexact type, so cast to any first
            overflowMenuObjectForType={overflowMenuObjectForType}
            includeRoutedModal={props.layout === 'FULL' || props.layout === 'REPOST_WITH_MODAL'}
            onCarouselClick={e => {
              logInteraction(e, 'batchGalleryClick', {
                slot: props.currentlyDisplayedImageIndex + 1, // the index is 0 based, slot needs to be 1 based
                itemCount: metadata.numPhotos,
              });
            }}
          />
        )}
      </InteractionLogger>
    </CardSection.Content>,
    <CardSection.Content key={`caption_${metadata.id}`}>
      <InteractionLogger>
        {logInteraction => (
          <div className={styles.caption}>
            {props.viewportCategory === 'MOBILE' &&
            currentCaption &&
            currentCaption.length > MAX_CAPTION_LENGTH ? (
              <ReadMore numLinesToShow={3} cta={cta()} ctaNewline>
                <Linkify
                  text={currentCaption}
                  socialReferences={currentSocialReferences}
                  useSpan
                  onLinkReferenceClick={(e, url) => {
                    logInteraction(e, 'linkReferenceClick', {
                      authorUserId: props.actor.userId,
                      url,
                    });
                  }}
                />
              </ReadMore>
            ) : (
              <Linkify
                text={currentCaption}
                socialReferences={currentSocialReferences}
                useSpan
                onLinkReferenceClick={(e, url) => {
                  logInteraction(e, 'linkReferenceClick', {
                    authorUserId: props.actor.userId,
                    url,
                  });
                }}
              />
            )}
          </div>
        )}
      </InteractionLogger>
    </CardSection.Content>,
  ];

  // If the batch has pois use those always, else return the pois of the currently visible photo
  const getPois = () => {
    if (metadata?.orderedLocations?.length > 0) {
      return metadata.orderedLocations;
    }

    return props.photos?.[props.currentlyDisplayedImageIndex]?.location
      ? [props.photos[props.currentlyDisplayedImageIndex].location]
      : [];
  };

  return (
    <CardSection
      isSingleObject={props.isSingleObject}
      header={header}
      content={content}
      socialStatisticsBar={props.socialStatisticsBar}
      socialActionBar={props.socialActionBar}
      pois={getPois()}
      classNames=""
      contentClass=""
      sponsorship={props.sponsorship}
    />
  );
};

MediaBatchPhotoSection.defaultProps = {
  isSingleObject: false,
  member: null,
  actor: null,
  actorForUrl: undefined,
  deleteObject: null,
  untagObject: null,
  loggedInUserId: null,
  viewportCategory: undefined,
  layout: 'FULL',
};

export default connect<Props, MediaBatchPhotoSectionProps, _, _, _, _>(state => ({
  loggedInUserId: state.auth.loggedInUserId,
  viewportCategory: state.meta.device.viewportCategory,
}))(MediaBatchPhotoSection);
